import React from "react";
import { cx, priceDisplay } from "@jugl-web/utils";
import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as HelpIcon } from "./assets/help.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import { ReactComponent as VisaIcon } from "./assets/visa.svg";
import { ReactComponent as MaestroIcon } from "./assets/maestro.svg";
import { ReactComponent as ExpressIcon } from "./assets/express.svg";
import { ReactComponent as MastercardIcon } from "./assets/mastercard.svg";
import { ReactComponent as StripeIcon } from "./assets/stripe.svg";
import { SelectedAddon } from "../../types";

export interface SubscriptionSummaryBasePlan {
  name: string;
  details: string;
  price: number;
  breakdown?: string;
  originalPrice?: number;
}

export const SubscriptionSummary: React.FC<{
  selected: Record<string, SelectedAddon>;
  total: number;
  basePlan: SubscriptionSummaryBasePlan;
  credits?: number;
  onCompletePurchase?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  paymentMethod?: { card: { display_brand: string; last4: string } } | null;
  onEditPaymentMethod?: () => void;
}> = ({
  total,
  selected,
  basePlan,
  credits,
  onCompletePurchase,
  isLoading,
  isDisabled,
  paymentMethod,
  onEditPaymentMethod,
}) => {
  return (
    <div className="flex h-full w-[528px] flex-col gap-10 bg-white">
      <div className="flex flex-col gap-1.5 px-8">
        <span className="pt-8 font-secondary text-2xl leading-[24px] font-medium text-dark">
          Summary
        </span>
      </div>
      <div className="h-px w-full bg-grey-300" />
      <div
        className={cx("flex flex-col gap-8 border-grey-300 px-8", {
          "py-0": Object.keys(selected).length === 0,
        })}
      >
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1.5">
              <span className="font-secondary text-lg leading-[27px] font-medium text-dark">
                {basePlan.name}
              </span>
              <span className="font-secondary text-sm text-dark-700">
                {basePlan.details}
              </span>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center gap-1.5">
                {basePlan.originalPrice != null && (
                  <span className="font-secondary text-base text-dark-500 line-through">
                    {priceDisplay(basePlan.originalPrice, "usd")}
                  </span>
                )}
                <span className="font-secondary text-xl font-medium text-dark">
                  {priceDisplay(basePlan.price, "usd")}
                </span>
              </div>
              {basePlan.breakdown && (
                <span className="font-secondary text-sm text-dark-600">
                  {basePlan.breakdown}
                </span>
              )}
            </div>
          </div>
        </div>
        {Object.entries(selected).map(([id, value]) => (
          <div className="flex items-start justify-between" key={id}>
            <div className="flex flex-col gap-1.5">
              <span className="font-secondary text-lg leading-[27px] font-medium text-dark">
                {value.addon.name}
              </span>
              <span className="font-secondary text-sm text-dark-700">
                {value.type === "quantity" && value.quantity}{" "}
                {value.type === "tier"
                  ? value.addon.tiers?.find((item) => item.id === value.tierId)
                      ?.badge
                  : value.addon.badge}
              </span>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className="font-secondary text-xl font-medium text-dark">
                $
                {value.type === "tier"
                  ? value.addon.tiers?.find((item) => item.id === value.tierId)
                      ?.price
                  : value.addon.price}
              </span>
              {value.type === "quantity" &&
                value.addon.billing === "monthly" && (
                  <span className="font-secondary text-sm text-dark-600">
                    (2 x 12 months)
                  </span>
                )}
            </div>
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-grey-300" />
      <div className="flex flex-col gap-3 px-8">
        {credits != null && credits > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-secondary text-base leading-[1.5] text-dark">
                Balance Applied
              </span>
              <Tooltip
                placement="top"
                className="max-w-[280px] rounded-[12px] p-3"
                renderTrigger={({ ref, props }) => (
                  <span
                    ref={ref}
                    {...props}
                    className="inline-flex shrink-0"
                    aria-label="Balance applied info"
                  >
                    <HelpIcon />
                  </span>
                )}
              >
                <span className="font-secondary text-sm leading-[1.5] text-white">
                  You have unused credit from a previous plan. It&apos;s
                  automatically applied to this payment.
                </span>
              </Tooltip>
            </div>
            <span className="font-secondary text-xl font-medium text-dark">
              -{priceDisplay(credits, "usd")}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="font-secondary text-xl font-medium text-dark">
            Total
          </span>
          <span className="font-secondary text-[22px] leading-[33px] font-semibold text-dark">
            {priceDisplay(total, "usd")}
          </span>
        </div>
      </div>
      {paymentMethod && (
        <div className="flex items-center gap-2 px-8">
          <span className="font-secondary text-sm text-dark-700">
            Payment method:
          </span>
          <span className="font-secondary text-sm font-medium text-dark capitalize">
            {paymentMethod.card.display_brand} **** {paymentMethod.card.last4}
          </span>
          {onEditPaymentMethod && (
            <PlainButton
              className="font-secondary text-sm font-medium text-primary"
              onClick={onEditPaymentMethod}
            >
              Edit
            </PlainButton>
          )}
        </div>
      )}
      <div className="flex flex-col gap-6 px-8">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isLoading}
          isDisabled={isDisabled}
          iconEnd={<ChevronIcon />}
          onClick={onCompletePurchase}
        >
          Complete Purchase
        </Button>
        <div className="flex flex-col items-start gap-4">
          <div className="flex items-center gap-1.5">
            <ShieldIcon />
            <span className="flex gap-1 font-secondary text-sm text-dark-600">
              Safe and secure payment, powered by
            </span>
            <StripeIcon className="mb-[-1px]" />
          </div>
          <div className="flex items-center gap-[14px]">
            <VisaIcon />
            <MastercardIcon />
            <MaestroIcon />
            <ExpressIcon />
          </div>
        </div>
      </div>
    </div>
  );
};
